import React, { useCallback, useContext, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AppContext, SnackbarType } from "../../../AppContext";
import FileUpload from "../../../components/FileUpload";
import { RenderByRole } from "../../../components/RenderByRole";
import { FarmTreeRoles } from "../../../consts/farm-tree-roles";
import { useAuth } from "../../../hooks/useAuth";
import { formatDate } from "../../../utils/format-date";
import { useCalibrationData } from "../api/useCalibrationData";
import { useCalibrationDelete } from "../api/useCalibrationDelete";
import { useCalibrationUpload } from "../api/useCalibrationUpload";

export type CalibrationUploadProps = {
  datasetId: number;
  cafsId: number;
  cafsType: "plant" | "input";
};

export function CalibrationUpload({
  datasetId,
  cafsId,
  cafsType,
}: CalibrationUploadProps) {
  const { user } = useAuth();
  const { formatMessage } = useIntl();
  const { showSnackbar } = useContext(AppContext);

  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);

  const fileUploadRef = useRef<HTMLInputElement>(null);

  const {
    calibrationData,
    hasCalibration,
    isPending: isLoadingCalibration,
  } = useCalibrationData({
    datasetId,
    cafsId,
    cafsType,
  });

  const updateProgress = useCallback((newProgress: number) => {
    setProgress(newProgress);
  }, []);

  const resetUploadState = useCallback(() => {
    setUploadStatus("Processing...");
    if (fileUploadRef.current) {
      fileUploadRef.current.value = "";
    }
  }, []);

  const { upload, isPending: isUploading } = useCalibrationUpload({
    datasetId,
    cafsId,
    cafsType,
    onProgress: updateProgress,
    onUploadComplete: resetUploadState,
  });

  const { deleteCalibration, isPending: isDeleting } = useCalibrationDelete({
    datasetId,
    cafsId,
    cafsType,
  });

  const handleUpload = useCallback(
    (file: File) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        showSnackbar({
          type: SnackbarType.Error,
          message: formatMessage({
            id: "CALIBRATION.INVALID_FILE_TYPE",
            defaultMessage:
              "The calibration file should be an Excel file (.xlsx or .xls)",
            description:
              "Error message when uploading a non-Excel calibration file",
          }),
        });
        return;
      }

      // If calibration exists, delete it first before uploading new one
      if (hasCalibration) {
        deleteCalibration(undefined, {
          onSuccess: () => {
            // After successful deletion, upload the new file
            const formData = new FormData();
            formData.append("File", file);
            upload(formData);
          },
        });
      } else {
        // No existing calibration, just upload
        const formData = new FormData();
        formData.append("File", file);
        upload(formData);
      }
    },
    [upload, hasCalibration, deleteCalibration, showSnackbar, formatMessage],
  );

  return (
    <RenderByRole role={user?.Role} allowedRoles={FarmTreeRoles}>
      <div className="mt-6 mb-6 w-full">
        <div className="flex flex-col gap-4 items-center">
          <div className="flex justify-center">
            <FileUpload
              ref={fileUploadRef}
              disabled={isUploading || isDeleting}
              onUpload={handleUpload}
              progress={progress}
              isPending={isUploading || isDeleting}
              status={uploadStatus}
              accept=".xlsx,.xls"
              color="secondary"
              variant="outlined"
              text={formatMessage({
                id: "CALIBRATION.UPLOAD_BUTTON",
                defaultMessage: "Upload Calibration Results",
                description: "Upload calibration button text",
              })}
            />
          </div>

          {!isLoadingCalibration && hasCalibration && calibrationData && (
            <div className="flex flex-row items-center gap-2">
              <span className="text-darkGray">
                <FormattedMessage
                  id="CALIBRATION.LAST_UPLOADED"
                  defaultMessage="Last uploaded:"
                  description="Last uploaded label"
                />
              </span>
              <span className="text-shark font-semibold text-sm">
                {formatDate(calibrationData.UpdatedAt)}
              </span>
            </div>
          )}
        </div>
      </div>
    </RenderByRole>
  );
}
